import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  MessageSquare,
  AlertCircle,
  Clock,
  User,
  Target,
  ExternalLink,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { PublicFeedbackForm } from '@/components/PublicFeedbackForm'
import { api } from '@/lib/api'

interface FeedbackQuestion {
  id: string
  text: string
  questionType: 'text' | 'rating' | 'multiple_choice'
  isRequired: boolean
  sortOrder: number
  options?: unknown
}

interface FeedbackCycleData {
  cycle: {
    id: string
    name: string
    description: string
    startDate: string
    endDate: string
    cycleType: string
    isAnonymous: boolean
    questions: FeedbackQuestion[]
    targetUser?: {
      id: string
      firstName: string
      lastName: string
      email: string
      avatar?: string
    }
    creator: {
      id: string
      firstName: string
      lastName: string
      email: string
    }
  }
  participant: {
    id: string
    user: {
      id: string
      firstName: string
      lastName: string
      email: string
      avatar?: string
    }
    invitedAt: string
    isCompleted: boolean
  }
  token: string
}

interface FeedbackResponse {
  success: boolean
  data?: FeedbackCycleData
  error?: string
}

export function PublicFeedbackPage() {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<FeedbackCycleData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setError('Invalid feedback link')
      setLoading(false)
      return
    }

    fetchFeedbackData(token)
  }, [token])

  const fetchFeedbackData = async (feedbackToken: string) => {
    try {
      setLoading(true)
      const result = await api.get<FeedbackResponse>(`/feedback/${feedbackToken}`)

      if (!result.success || !result.data) {
        setError(result.error || 'Failed to load feedback form')
        return
      }

      const normalizedQuestions: FeedbackQuestion[] = Array.isArray(result.data.cycle.questions)
        ? result.data.cycle.questions.map((question: any, index: number) => ({
            id: String(question?.id ?? `question-${index}`),
            text: String(question?.text ?? ''),
            questionType: (question?.questionType ?? question?.type ?? 'text') as FeedbackQuestion['questionType'],
            isRequired: question?.isRequired !== false,
            sortOrder: typeof question?.sortOrder === 'number' ? question.sortOrder : index,
            options: question?.options ?? null,
          }))
        : []

      setData({
        ...result.data,
        cycle: {
          ...result.data.cycle,
          questions: normalizedQuestions,
        },
      })
    } catch (err) {
      console.error('Error fetching feedback data', err)
      setError('Failed to load feedback form')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">Loading feedback form...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-destructive/8 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Access Error</h2>
              <p className="text-muted-foreground mb-6">{error}</p>

              {error.includes('ended') && (
                <p className="text-sm text-muted-foreground">
                  This feedback cycle has ended. Please contact your administrator for assistance.
                </p>
              )}

              <Button variant="outline" className="mt-6" asChild>
                <Link to="/">
                  Return to Homepage
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!data) {
    return null
  }

  const { cycle, participant } = data

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 py-12">
      <div className="container max-w-5xl mx-auto px-4 space-y-8">
        <Card className="bg-card/90 backdrop-blur border border-border/60 shadow-xl">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <Badge variant="outline" className="mb-3 uppercase tracking-wide">
                  {cycle.cycleType} cycle
                </Badge>
                <CardTitle className="text-3xl font-bold text-foreground mb-2">
                  {cycle.name}
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground">
                  {cycle.description}
                </CardDescription>
              </div>
              <div className="bg-muted/40 border border-border rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <User className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Feedback for</p>
                    <p className="font-semibold text-foreground">
                      {cycle.targetUser ? `${cycle.targetUser.firstName} ${cycle.targetUser.lastName}` : 'Team Member'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Due</p>
                    <p className="font-semibold text-foreground">
                      {new Date(cycle.endDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-xl bg-primary/5 border border-primary/10 p-4">
                <MessageSquare className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Feedback participant</p>
                  <p className="text-sm text-muted-foreground">
                    {participant.user.firstName} {participant.user.lastName} ({participant.user.email})
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-muted/40 border border-border p-4">
                <Target className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Cycle creator</p>
                  <p className="text-sm text-muted-foreground">
                    {cycle.creator.firstName} {cycle.creator.lastName}
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <ExternalLink className="w-4 h-4" /> Submission details
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>Anonymous feedback: {cycle.isAnonymous ? 'Enabled' : 'Disabled'}</li>
                <li>Submitted responses will be reviewed by your manager</li>
                <li>All questions are required unless marked optional</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <PublicFeedbackForm cycle={cycle} participant={participant} token={data.token} />
      </div>
    </div>
  )
}

export default PublicFeedbackPage
